import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Luggage, Shirt, Headphones, Camera, MapPin } from 'lucide-react';

interface PackingLoadingAnimationProps {
  onComplete: () => void;
}

export function PackingLoadingAnimation({ onComplete }: PackingLoadingAnimationProps) {
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(0);

  const messages = [
    "Analyzing your destination...",
    "Checking weather forecasts...",
    "Customizing your packing list...",
    "Adding essential items...",
    "Almost ready!"
  ];

  const floatingIcons = [
    { Icon: Shirt, delay: 0 },
    { Icon: Headphones, delay: 0.1 },
    { Icon: Camera, delay: 0.2 },
    { Icon: MapPin, delay: 0.3 },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      onComplete();
    }, 3000);

    const progressTimer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressTimer);
          return 100;
        }
        return prev + 2;
      });
    }, 60);

    const messageTimer = setInterval(() => {
      setCurrentMessage(prev => {
        if (prev >= messages.length - 1) {
          clearInterval(messageTimer);
          return prev;
        }
        return prev + 1;
      });
    }, 600);

    return () => {
      clearTimeout(timer);
      clearInterval(progressTimer);
      clearInterval(messageTimer);
    };
  }, [onComplete]);

  return (
    <div className="w-full max-w-4xl mx-auto px-4">
      <div className="min-h-[50vh] flex flex-col items-center justify-center space-y-8 py-8">
        
        {/* Floating Icons */}
        <div className="flex items-center justify-center gap-1">
          {floatingIcons.map((item, index) => (
            <motion.div
              key={index}
              initial={{ y: 0 }}
              animate={{ y: [-8, 8, -8] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: item.delay,
                ease: "easeInOut"
              }}
              className="w-10 h-10 bg-white rounded-full shadow-md flex items-center justify-center"
            >
              <item.Icon className="w-5 h-5 text-[#5CBFB3]" strokeWidth={1.5} />
            </motion.div>
          ))}
        </div>

        {/* Teal Suitcase Card */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="w-56 h-40 rounded-3xl bg-gradient-to-br from-[#6DD5C7] to-[#4BA8A0] shadow-xl flex items-center justify-center"
        >
          <Luggage className="w-16 h-16 text-white" strokeWidth={1.5} />
        </motion.div>

        {/* Progress Bar */}
        <div className="w-full max-w-md">
          <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[#5CBFB3] to-[#4BA8A0] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>
        </div>

        {/* Loading Message */}
        <div className="text-center space-y-2">
          <motion.p
            key={currentMessage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xl font-medium text-foreground"
          >
            {messages[currentMessage]}
          </motion.p>
          <p className="text-base text-muted-foreground">
            {Math.min(Math.round(progress), 100)}% complete
          </p>
        </div>
      </div>
    </div>
  );
}
